import {
  DEFAULT_FOLLOW_UP_DELAY_HOURS,
  isWithinFollowUpDelay,
} from '@/features/submissions/domain/submissionStage'

export type ClientType = 'RESIDENTIAL' | 'COMMERCIAL'
export type ClientStatus = 'ACTIVE' | 'SUSPENDED' | 'INACTIVE' | 'ARCHIVED'
export type ClientLanguage = 'FR' | 'EN'

export const clientStatusLabels: Record<ClientStatus, string> = {
  ACTIVE: 'Actif',
  SUSPENDED: 'Suspendu',
  INACTIVE: 'Inactif',
  ARCHIVED: 'Archivé',
}

/** Motif obligatoire pour ces deux statuts uniquement (spec section 8). */
export function clientStatusRequiresMotif(status: ClientStatus): boolean {
  return status === 'SUSPENDED' || status === 'ARCHIVED'
}

export interface ClientAddress {
  line1: string
  /** Appartement, bureau ou case postale (`clients.adresse_ligne2`). */
  line2?: string
  city: string
  province: string
  postalCode: string
  latitude?: number
  longitude?: number
}

export interface ClientRecord {
  id: string
  number: string
  type: ClientType
  status: ClientStatus
  language: ClientLanguage
  displayName: string
  firstName?: string
  lastName?: string
  companyName?: string
  phone: string
  mobilePhone?: string
  email?: string
  address?: ClientAddress
  statutMotif?: string
  createdAt: string
  updatedAt: string
}

export interface UpdateClientStatusCommand {
  clientId: string
  status: ClientStatus
  motif?: string
}

/**
 * Mise à jour ciblée d'une fiche Client existante. Seuls les champs fournis
 * sont écrits ; les autres colonnes restent intactes. Utilisée par l'assistant
 * de Contrat quand il réutilise un Client dont l'adresse ou le téléphone
 * saisis diffèrent de la fiche.
 */
export interface UpdateClientCommand {
  clientId: string
  firstName?: string
  lastName?: string
  companyName?: string
  phone?: string
  mobilePhone?: string
  email?: string
  address?: string
  /** Clé absente : intacte; chaîne vide : effacée. */
  addressLine2?: string
  city?: string
  province?: string
  postalCode?: string
  latitude?: number
  longitude?: number
}

export interface UpdateClientResult {
  clientId: string
  updatedAt: string
}

/**
 * État métier d'un Client dans la liste. Il est dérivé des Contrats déjà
 * existants (`contracts.statut` legacy et `contracts.status_v2`) : aucun
 * nouvel objet métier n'est créé pour l'afficher. `Actif` seul ne dit que
 * « fiche non archivée » et n'a pas sa place dans une carte.
 */
export type ClientContractState = 'ACTIVE' | 'AWAITING_PAYMENT' | 'QUOTE_PENDING' | 'NONE'

export const clientContractStateLabels: Record<ClientContractState, string> = {
  ACTIVE: 'Contrat actif',
  AWAITING_PAYMENT: 'En attente de paiement',
  QUOTE_PENDING: 'Soumission en cours',
  NONE: 'Sans contrat',
}

/** Ligne `contracts` réduite aux colonnes qui portent l'état métier. */
export interface ClientContractSummary {
  /** Présents seulement lorsque la lecture les demande (liste Clients : lien vers la fiche Contrat). */
  id?: string
  number?: string
  legacyStatus: string
  statusV2?: string
  season?: string
  /** Requis pour distinguer « envoyée » de « à relancer » (délai dépassé). */
  sentAt?: string
  /** Contrat « sans taxes » (bouton $$$) — même donnée que `ContractRecord.sansFacturation`. */
  sansFacturation?: boolean
}

const quotePendingStatusV2 = ['PENDING_CLIENT', 'VIEWED', 'SIGNED']
const quotePendingLegacyStatus = ['brouillon', 'a_signer', 'en_attente']

function stateOfContract(contract: ClientContractSummary): ClientContractState {
  if (contract.statusV2 === 'ACTIVE' || contract.legacyStatus === 'actif') return 'ACTIVE'
  if (contract.statusV2 === 'AWAITING_PAYMENT') return 'AWAITING_PAYMENT'
  if (contract.statusV2 && quotePendingStatusV2.includes(contract.statusV2)) return 'QUOTE_PENDING'
  if (quotePendingLegacyStatus.includes(contract.legacyStatus)) return 'QUOTE_PENDING'
  return 'NONE'
}

const statePriority: ClientContractState[] = ['ACTIVE', 'AWAITING_PAYMENT', 'QUOTE_PENDING', 'NONE']

/**
 * Un Client peut porter plusieurs Contrats (saisons, renouvellements) : le
 * plus engageant l'emporte, et la saison affichée est celle de ce Contrat.
 */
/** Contrats actifs d'un Client (même règle que l'état « Contrat actif »), pour la colonne Contrats. */
export function countActiveContracts(contracts: readonly ClientContractSummary[]): number {
  return contracts.filter((contract) => stateOfContract(contract) === 'ACTIVE').length
}

export function deriveClientContractState(contracts: readonly ClientContractSummary[]): {
  state: ClientContractState
  season?: string
} {
  let best: { state: ClientContractState; season?: string } = { state: 'NONE' }
  for (const contract of contracts) {
    const state = stateOfContract(contract)
    if (statePriority.indexOf(state) < statePriority.indexOf(best.state)) {
      best = { state, ...(contract.season ? { season: contract.season } : {}) }
    }
  }
  return best
}

export interface ClientContractReference {
  id: string
  number: string
}

export interface ClientListItem extends ClientRecord {
  statusLabel?: string
  activeContract?: string
  route?: string
  balanceLabel?: string
  /** Absent lorsque les Contrats ne sont pas lisibles par la session. */
  contractState?: ClientContractState
  contractSeason?: string
  /** Absent lorsque les Contrats ne sont pas lisibles par la session. */
  dossierStatus?: ClientDossierStatus
  /**
   * VIP : le Contrat actif du Client a été activé via le bouton $$$ (« sans
   * taxes », `sansFacturation`) — disparaît dès que ce Contrat se termine ou
   * s'annule (n'est plus le Contrat actif). Absent lorsque les Contrats ne
   * sont pas lisibles par la session.
   */
  vip?: boolean
  /** Nombre de Contrats actifs. Absent lorsque les Contrats ne sont pas lisibles par la session. */
  activeContractCount?: number
  /** Contrats qui portent `dossierStatus`, affichés en liens dans la liste. Absent comme `dossierStatus`. */
  dossierContracts?: ClientContractReference[]
  /**
   * Solde dû : somme des soldes des Factures émises non réglées (ni brouillon ni annulée). Absent si
   * les Factures ne sont pas lisibles — affiché « — », jamais 0.
   */
  balanceDueCents?: number
  /** Portion échue du solde dû (échéance passée, fuseau de Toronto). Même périmètre que `balanceDueCents`. */
  overdueCents?: number
}

/**
 * VIP client = son Contrat actif (même sélection que `deriveClientContractState`)
 * porte `sansFacturation`. Jamais un statut propre au Client — dérivé à
 * chaque lecture, disparaît avec le Contrat qui le portait.
 */
export function deriveClientVip(contracts: readonly ClientContractSummary[]): boolean {
  return contracts.some(
    (contract) => stateOfContract(contract) === 'ACTIVE' && contract.sansFacturation === true,
  )
}

/**
 * Refonte Clients, Phase 3 (audit UX/UI, §5.3) : le statut affiché en liste et
 * en fiche doit raconter le dossier commercial réel — « Actif » seul ne dit
 * pas si un Contrat attend encore son premier paiement. Dérivé des mêmes
 * Contrats que `deriveClientContractState`, plus fin (7 états au lieu de 4);
 * ne remplace pas `ClientContractState` (toujours utilisé ailleurs) ni le
 * statut technique `ClientStatus` (actif/archivé, secondaire — menu
 * d'administration seulement).
 */
export type ClientDossierStatus =
  'NONE' | 'DRAFT' | 'SENT' | 'FOLLOW_UP' | 'AWAITING_PAYMENT' | 'ACTIVE' | 'SUSPENDED'

export const clientDossierStatusLabel: Record<ClientDossierStatus, string> = {
  NONE: 'Sans proposition',
  DRAFT: 'Proposition en préparation',
  SENT: 'Proposition envoyée',
  FOLLOW_UP: 'À signer — à relancer',
  AWAITING_PAYMENT: 'Paiement attendu',
  ACTIVE: 'Contrat actif',
  SUSPENDED: 'Contrat suspendu',
}

/** Bouton d'action unique de la fiche Client (§5.3, « Action contextuelle »). */
export const clientDossierNextActionLabel: Record<ClientDossierStatus, string> = {
  NONE: 'Créer une proposition',
  DRAFT: 'Continuer la proposition',
  SENT: 'Relancer le client',
  FOLLOW_UP: 'Relancer le client',
  AWAITING_PAYMENT: 'Enregistrer un paiement',
  ACTIVE: 'Voir le contrat',
  SUSPENDED: 'Régulariser le paiement',
}

const suspendedStatusV2 = ['SUSPENDED']
const suspendedLegacyStatus = ['suspendu']
const awaitingPaymentStatusV2 = ['SIGNED', 'AWAITING_PAYMENT']
const sentStatusV2 = ['PENDING_CLIENT', 'VIEWED']
const sentLegacyStatus = ['a_signer', 'en_attente']
const draftLegacyStatus = ['brouillon']

/** Contrat dont dérive `deriveClientDossierStatus` : même colonnes que `ClientContractSummary`. */
function dossierStatusOfContract(contract: ClientContractSummary, now: Date): ClientDossierStatus {
  if (contract.statusV2 === 'ACTIVE' || contract.legacyStatus === 'actif') return 'ACTIVE'
  if (contract.statusV2 && suspendedStatusV2.includes(contract.statusV2)) return 'SUSPENDED'
  if (suspendedLegacyStatus.includes(contract.legacyStatus)) return 'SUSPENDED'
  if (contract.statusV2 && awaitingPaymentStatusV2.includes(contract.statusV2)) return 'AWAITING_PAYMENT'
  if (contract.statusV2 && sentStatusV2.includes(contract.statusV2)) {
    return isWithinFollowUpDelay(contract.sentAt, DEFAULT_FOLLOW_UP_DELAY_HOURS, now) ? 'SENT' : 'FOLLOW_UP'
  }
  if (sentLegacyStatus.includes(contract.legacyStatus)) {
    return isWithinFollowUpDelay(contract.sentAt, DEFAULT_FOLLOW_UP_DELAY_HOURS, now) ? 'SENT' : 'FOLLOW_UP'
  }
  if (contract.statusV2 === 'DRAFT' || draftLegacyStatus.includes(contract.legacyStatus)) return 'DRAFT'
  return 'NONE'
}

const dossierStatusPriority: ClientDossierStatus[] = [
  'SUSPENDED',
  'AWAITING_PAYMENT',
  'ACTIVE',
  'FOLLOW_UP',
  'SENT',
  'DRAFT',
  'NONE',
]

/**
 * Même sélection que `deriveClientContractState` (le Contrat le plus engagé
 * l'emporte), mais 7 états au lieu de 4 — voir `ClientDossierStatus`. Un
 * Contrat suspendu prime sur tout le reste : c'est le signal le plus urgent
 * pour l'employé qui consulte la liste.
 */
export function deriveClientDossierStatus(
  contracts: readonly ClientContractSummary[],
  now: Date = new Date(),
): ClientDossierStatus {
  let best: ClientDossierStatus = 'NONE'
  for (const contract of contracts) {
    const status = dossierStatusOfContract(contract, now)
    if (dossierStatusPriority.indexOf(status) < dossierStatusPriority.indexOf(best)) {
      best = status
    }
  }
  return best
}

/**
 * Contrats qui portent le statut de dossier affiché (`deriveClientDossierStatus`) : proposition envoyée, paiement
 * attendu, actif, suspendu… Seuls ceux dont l'identifiant et le numéro sont lus, pour les lier depuis la liste Clients.
 */
export function dossierContractReferences(
  contracts: readonly ClientContractSummary[],
  now: Date = new Date(),
): ClientContractReference[] {
  const status = deriveClientDossierStatus(contracts, now)
  if (status === 'NONE') return []
  const references: ClientContractReference[] = []
  for (const contract of contracts) {
    if (dossierStatusOfContract(contract, now) !== status || !contract.id || !contract.number) continue
    references.push({ id: contract.id, number: contract.number })
  }
  return references
}

export interface ClientStats {
  total: number
  active: number
  inactive: number
  residential: number
  commercial: number
  /** Absent sans permission `contract.read`. */
  activeContracts?: number
  /** VIP (bouton $$$, `sansFacturation`) — absent sans permission `contract.read`. */
  vip?: number
}

export type ClientDataSource = 'fixtures' | 'supabase' | 'fixture-fallback'

export interface ClientQueryMeta {
  source: ClientDataSource
  retrievedAt: string
  fallbackReason?: string
  anomalies: number
}

export interface ClientPage {
  items: ClientListItem[]
  page: number
  pageSize: number
  total: number
  stats: ClientStats
  meta: ClientQueryMeta
}

export interface ClientDetailResult {
  client: ClientRecord
  meta: ClientQueryMeta
}

export interface ClientListFilters {
  page: number
  pageSize: number
  search?: string
  status?: ClientStatus
  type?: ClientType
  /**
   * Joint l'état Contrat de chaque Client de la page. La liste s'affiche sans
   * lui : la session qui n'a pas `contract.read` ne verrait que des lignes
   * vides, ce qui serait faux plutôt que discret.
   */
  includeContractState?: boolean
  /**
   * Refonte Clients, Phase 3 (audit UX/UI §5.3) : filtre par dossier
   * commercial plutôt que par statut technique. Implique `includeContractState`.
   */
  dossierStatus?: ClientDossierStatus
  /**
   * Rattrapage chantier `audit-ui-v2` (item D) : filtre Renouvellements —
   * identifiants déjà résolus côté page (candidats de la campagne ouverte),
   * appliqués ici en `.in('id', ...)` plutôt qu'après la pagination.
   */
  clientIds?: string[]
  /** Contrat actif du Client activé via le bouton $$$ (`sansFacturation`). Implique `includeContractState`. */
  vip?: boolean
  /**
   * Vues « Avec contrat actif » / « Sans contrat actif » : Clients distincts, même règle que l'état
   * « Contrat actif ». Implique `includeContractState`.
   */
  contractActivity?: 'WITH_ACTIVE' | 'WITHOUT_ACTIVE'
  /** Joint le solde dû des Factures de la page (session avec `invoice.read`). */
  includeBalance?: boolean
}

/** Langues du registre legacy; aucune autre valeur n'est acceptée en base. */
export const clientLanguages = ['francais', 'anglais'] as const

export interface CreateClientCommand {
  idempotencyKey: string
  firstName?: string
  lastName?: string
  company?: string
  /** Exigé : sans lui, le Client n'apparaît pas dans le modèle de lecture V3. */
  phone: string
  mobilePhone?: string
  email?: string
  address?: string
  /** Appartement, bureau ou case postale. */
  addressLine2?: string
  city?: string
  postalCode?: string
  /** Coordonnées Mapbox de l'adresse choisie — sans elles, l'outil de mesure
   * satellite ne peut pas géolocaliser la propriété du Client. */
  latitude?: number
  longitude?: number
  /** Exigé pour la même raison que le téléphone. */
  clientType: string
  language?: (typeof clientLanguages)[number]
  /** Confirme sciemment la création malgré des doublons possibles, section 53. */
  acknowledgeDuplicates?: boolean
  /**
   * Rattrapage chantier `audit-ui-v2` (item C) : Demande d'origine — pose
   * `clients.source_lead_id` (migration `20260916010000`, posée en Phase 2,
   * jamais câblée côté front jusqu'ici). Ne fait jamais progresser le statut
   * de la Demande (contrairement à `convert_lead_to_client_v1`).
   */
  leadId?: string
}

export interface CreateClientResult {
  clientId: string
  number: string
  displayName: string
}

export interface ClientDuplicateSuggestion {
  clientId: string
  number: string | null
  displayName: string | null
  email: string | null
  phone: string | null
  matchedOn: 'PHONE' | 'EMAIL' | 'ADDRESS'
}
